import {
  getString,
  getObject,
  getLeoUuid,
  LeoUUID,
  getOptionalObject,
  getBoolean,
  getOptionalBoolean,
} from "@surya-digital/leo-ts-types";
import { JsonVersion, JsonVersionDTO } from "../types/jsonVersion";
import { QuestionType } from "../types/questionType";
import { Code, CodeDTO } from "../types/code";
import { SurveyLocalizedText, SurveyLocalizedTextDTO } from "../assets/surveyLocalizedText";

export interface RespondentQuestionDetailsDTO {
  questionDetailsJSON: string;
  jsonVersion: JsonVersionDTO;
  questionType: QuestionType.QuestionTypeDTO;
  questionId: string;
  questionCode: CodeDTO;
  question?: SurveyLocalizedTextDTO | null;
  questionDescription?: SurveyLocalizedTextDTO | null;
  isMandatory: boolean;
  isUnanswered?: boolean | null;
}

export class RespondentQuestionDetails {
  readonly isRespondentQuestionDetailsRespondentQuestionDetails: boolean = true;

  readonly questionDetailsJSON: string;
  readonly jsonVersion: JsonVersion;
  readonly questionType: QuestionType.QuestionType;
  readonly questionId: LeoUUID;
  readonly questionCode: Code;
  readonly question: SurveyLocalizedText | null;
  readonly questionDescription: SurveyLocalizedText | null;
  readonly isMandatory: boolean;
  readonly isUnanswered: boolean | null;


  constructor(
  questionDetailsJSON: string, 
  jsonVersion: JsonVersion, 
  questionType: QuestionType.QuestionType, 
  questionId: LeoUUID, 
  questionCode: Code, 
  question: SurveyLocalizedText | null = null, 
  questionDescription: SurveyLocalizedText | null = null, 
  isMandatory: boolean, 
  isUnanswered: boolean | null = null
  ) {
    RespondentQuestionDetails.validateQuestionDetailsJson(questionDetailsJSON);
    this.questionDetailsJSON = questionDetailsJSON;
    this.jsonVersion = jsonVersion;
    this.questionType = questionType;
    this.questionId = questionId;
    this.questionCode = questionCode;
    this.question = question;
    this.questionDescription = questionDescription;
    this.isMandatory = isMandatory;
    this.isUnanswered = isUnanswered;
  }

  static fromDTO(dto: RespondentQuestionDetailsDTO): RespondentQuestionDetails {
    const questionDetailsJSON = getString(dto, "questionDetailsJSON", InvalidRespondentQuestionDetailsQuestionDetailsJsonError);
    const jsonVersion = JsonVersion.fromDTO(getObject(dto, "jsonVersion", InvalidRespondentQuestionDetailsJsonVersionError));
    const questionType = QuestionType.fromDTO(getObject(dto, "questionType", InvalidRespondentQuestionDetailsQuestionTypeError));
    const questionId = getLeoUuid(dto, "questionId", InvalidRespondentQuestionDetailsQuestionIdError);
    const questionCode = Code.fromDTO(getObject(dto, "questionCode", InvalidRespondentQuestionDetailsQuestionCodeError));
    let question = getOptionalObject(dto, "question");
    if(question !== undefined && question !== null) {
      question = SurveyLocalizedText.fromDTO(getObject(dto, "question", InvalidRespondentQuestionDetailsQuestionError));
    }
    let questionDescription = getOptionalObject(dto, "questionDescription");
    if(questionDescription !== undefined && questionDescription !== null) {
      questionDescription = SurveyLocalizedText.fromDTO(getObject(dto, "questionDescription", InvalidRespondentQuestionDetailsQuestionDescriptionError));
    }
    const isMandatory = getBoolean(dto, "isMandatory", InvalidRespondentQuestionDetailsIsMandatoryError);
    const isUnanswered = getOptionalBoolean(dto, "isUnanswered", InvalidRespondentQuestionDetailsIsUnansweredError);
    return new RespondentQuestionDetails(
      questionDetailsJSON,
      jsonVersion,
      questionType,
      questionId,
      questionCode,
      question,
      questionDescription,
      isMandatory,
      isUnanswered
    );
  }

  toDTO(): RespondentQuestionDetailsDTO {
    const dto: RespondentQuestionDetailsDTO = {
        questionDetailsJSON: this.questionDetailsJSON,
        jsonVersion: this.jsonVersion.toDTO(),
        questionType: QuestionType.toDTO(this.questionType),
        questionId: this.questionId.toDTO(),
        questionCode: this.questionCode.toDTO(),
        isMandatory: this.isMandatory,
    };
    if (this.question !== undefined && this.question !== null){
      dto.question = this.question.toDTO();
    }
    if (this.questionDescription !== undefined && this.questionDescription !== null){
      dto.questionDescription = this.questionDescription.toDTO();
    }
    if (this.isUnanswered !== undefined && this.isUnanswered !== null){
      dto.isUnanswered = this.isUnanswered;
    }
    return dto;
  }
  private static validateQuestionDetailsJson = (questionDetailsJSON: string): void => {
    if(questionDetailsJSON.trim().length === 0) {
      throw new InvalidRespondentQuestionDetailsQuestionDetailsJsonNonEmptyError(
        `Attribute questionDetailsJSON cannot be an empty string.`
      );
    }
  }
  
}

RespondentQuestionDetails.prototype.toString = function toString(): string {
  return `RespondentQuestionDetails(questionDetailsJSON='${this.questionDetailsJSON}',jsonVersion='${this.jsonVersion}',questionType='${this.questionType}',questionId='${this.questionId}',questionCode='${this.questionCode}',question='${this.question}',questionDescription='${this.questionDescription}',isMandatory='${this.isMandatory}',isUnanswered='${this.isUnanswered}')`;
};

export class InvalidRespondentQuestionDetailsError extends Error {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRespondentQuestionDetailsError";
  }
}
export class InvalidRespondentQuestionDetailsQuestionDetailsJsonError extends InvalidRespondentQuestionDetailsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRespondentQuestionDetailsQuestionDetailsJsonError";
  }
}

export class InvalidRespondentQuestionDetailsQuestionDetailsJsonNonEmptyError extends InvalidRespondentQuestionDetailsQuestionDetailsJsonError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRespondentQuestionDetailsQuestionDetailsJsonNonEmptyError";
  }
}

export class InvalidRespondentQuestionDetailsJsonVersionError extends InvalidRespondentQuestionDetailsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRespondentQuestionDetailsJsonVersionError";
  }
}

export class InvalidRespondentQuestionDetailsQuestionTypeError extends InvalidRespondentQuestionDetailsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRespondentQuestionDetailsQuestionTypeError";
  }
}

export class InvalidRespondentQuestionDetailsQuestionIdError extends InvalidRespondentQuestionDetailsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRespondentQuestionDetailsQuestionIdError";
  }
}

export class InvalidRespondentQuestionDetailsQuestionCodeError extends InvalidRespondentQuestionDetailsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRespondentQuestionDetailsQuestionCodeError";
  }
}

export class InvalidRespondentQuestionDetailsQuestionError extends InvalidRespondentQuestionDetailsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRespondentQuestionDetailsQuestionError";
  }
}

export class InvalidRespondentQuestionDetailsQuestionDescriptionError extends InvalidRespondentQuestionDetailsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRespondentQuestionDetailsQuestionDescriptionError";
  }
}

export class InvalidRespondentQuestionDetailsIsMandatoryError extends InvalidRespondentQuestionDetailsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRespondentQuestionDetailsIsMandatoryError";
  }
}

export class InvalidRespondentQuestionDetailsIsUnansweredError extends InvalidRespondentQuestionDetailsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRespondentQuestionDetailsIsUnansweredError";
  }
}
